import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "motion/react";
import { useMemo } from "react";

import { ButtonRouter } from "~/components/ui/button";
import { Footer } from "~/components/ui/footer";

const HEADLINES = [
  "Either they took a sabbatical, or this link took a wrong turn. Both happen.",
  "We searched high and low. No coach by that name in our orbit.",
  "Maybe they're off on a vision quest. Maybe the URL is just gremlins.",
  "This corner of the platform is suspiciously coach-free.",
];

function pickHeadline(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return HEADLINES[Math.abs(h) % HEADLINES.length];
}

interface Props {
  username: string;
}

export function CoachNotFound({ username }: Props) {
  const subhead = pickHeadline(username);
  return (
    <>
      <main className="relative isolate flex min-h-[calc(100vh-200px)] w-full flex-col items-center justify-center overflow-hidden px-4 py-16 sm:py-24">
        <Sparkles />
        <Backglow />

        <div className="relative z-10 flex w-full max-w-3xl flex-col items-center text-center">
          <PathToNowhere />

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
            className="text-text-primary mt-6 text-4xl font-[var(--font-heading)] font-medium tracking-tight text-balance sm:text-6xl"
          >
            Hmm. We can't find that coach.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.5, ease: "easeOut" }}
            className="text-text-secondary mt-5 max-w-xl text-base text-balance sm:text-lg"
          >
            {subhead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              delay: 1.05,
              duration: 0.4,
              ease: [0.34, 1.56, 0.64, 1],
            }}
            whileHover={{
              rotate: [0, -3, 3, -2, 2, 0],
              transition: { duration: 0.5 },
            }}
            className="mt-7 inline-flex max-w-full items-center gap-1 rounded-full border border-purple-200 bg-purple-50/70 px-4 py-1.5 text-sm font-medium text-purple-700 shadow-sm backdrop-blur-sm"
          >
            <span className="text-purple-400">/</span>
            <span className="truncate">{username}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.5, ease: "easeOut" }}
            className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:gap-4"
          >
            <ButtonRouter to="/discovery" variant="primary" size="lg">
              Find your coach
            </ButtonRouter>
            <ButtonRouter to="/coaches" variant="default" size="lg">
              Browse all coaches
            </ButtonRouter>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.4 }}
            className="mt-8"
          >
            <Link
              to="/"
              className="text-text-tertiary hover:text-text-primary text-sm underline-offset-4 hover:underline"
            >
              ← Back home
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer className="relative" />
    </>
  );
}

function Backglow() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10"
      style={{
        background:
          "radial-gradient(ellipse 80% 50% at 50% 35%, oklch(0.95 0.05 305) 0%, transparent 65%)",
      }}
    />
  );
}

function Sparkles() {
  const reduced = useReducedMotion();
  const dots = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        top: `${((i * 41 + 7) % 90) + 4}%`,
        left: `${((i * 67 + 19) % 92) + 3}%`,
        size: 2 + (i % 4),
        delay: (i % 7) * 0.3,
        duration: 3 + (i % 5) * 0.8,
      })),
    [],
  );

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-purple-400"
          style={{
            top: d.top,
            left: d.left,
            width: d.size,
            height: d.size,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={
            reduced
              ? { opacity: 0.4, scale: 1 }
              : {
                  opacity: [0, 0.6, 0],
                  scale: [0.4, 1, 0.4],
                  y: [0, -6, 0],
                }
          }
          transition={{
            duration: d.duration,
            delay: d.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

function PathToNowhere() {
  const reduced = useReducedMotion();
  return (
    <svg
      width="360"
      height="180"
      viewBox="0 0 360 180"
      fill="none"
      className="w-full max-w-[360px]"
      role="img"
      aria-label="A meandering dotted path leading to an empty circle"
    >
      <defs>
        <linearGradient id="pathGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="oklch(0.81 0.12 308)" />
          <stop offset="100%" stopColor="oklch(0.49 0.25 300)" />
        </linearGradient>
      </defs>

      {/* Meandering dotted path */}
      <motion.path
        d="M 20 110 C 60 30, 110 170, 170 80 C 220 10, 245 165, 285 95"
        stroke="url(#pathGrad)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="1 9"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{
          pathLength: { duration: reduced ? 0 : 1.6, ease: "easeInOut" },
          opacity: { duration: 0.3 },
        }}
      />

      {/* Empty avatar circle - dashed, slowly rotating */}
      <motion.g
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: reduced ? 0 : 1.4, duration: 0.5 }}
        style={{ transformOrigin: "310px 95px", transformBox: "fill-box" }}
      >
        {/* outer rotating dashed ring */}
        <motion.circle
          cx="310"
          cy="95"
          r="32"
          stroke="oklch(0.49 0.25 300)"
          strokeWidth="1.5"
          strokeDasharray="4 6"
          fill="none"
          animate={reduced ? {} : { rotate: 360 }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "310px 95px", transformBox: "fill-box" }}
        />
        {/* breathing fill ring */}
        <motion.circle
          cx="310"
          cy="95"
          r="26"
          fill="oklch(0.97 0.02 308)"
          stroke="oklch(0.81 0.12 308)"
          strokeWidth="1"
          animate={
            reduced
              ? {}
              : {
                  scale: [1, 1.06, 1],
                  opacity: [0.7, 1, 0.7],
                }
          }
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "310px 95px", transformBox: "fill-box" }}
        />
        {/* question mark */}
        <motion.text
          x="310"
          y="103"
          textAnchor="middle"
          fontSize="28"
          fontWeight="500"
          fill="oklch(0.49 0.25 300)"
          fontFamily="var(--font-heading)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: reduced ? 0 : 1.8, duration: 0.4 }}
        >
          ?
        </motion.text>
      </motion.g>
    </svg>
  );
}
